import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trans, useTranslation } from 'react-i18next';
import { Mail, ArrowRight, CheckCircle2 } from 'lucide-react';
import {
  useConsumeMagicLink,
  useMe,
  useRequestMagicLink,
} from '../api/use-marketplace';
import { SiteShell } from '../components/site-shell';

/**
 * /login page. Two states:
 *   1) ?token=… present → consume the magic-link, redirect to ?next or /me/bookings
 *   2) no token → render the email-request form
 *
 * After a successful request we show the "check your email" confirmation
 * in-place so the user doesn't lose context.
 */
export function LoginPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const next = params.get('next') ?? '/me/bookings';

  if (token) return <ConsumeMagicLink token={token} next={next} />;
  return <RequestMagicLink next={next} />;
}

// ─── Request form ──────────────────────────────────────────────────────

function RequestMagicLink({ next }: { next: string }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const req = useRequestMagicLink();

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    req.mutate(email.trim(), {
      onSuccess: () => setSent(true),
    });
  };

  return (
    <SiteShell>
      <section className="max-w-md mx-auto px-6 py-20 md:py-28">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-4 text-center">
            {t('login.eyebrow')}
          </p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground text-center mb-3 leading-tight">
            {t('login.titleA')} <span className="text-primary">{t('login.titleB')}</span><br />
            {t('login.titleC')}
          </h1>
          <p className="text-muted-foreground text-center mb-10 leading-relaxed">
            {t('login.subtitle')}
          </p>

          {sent ? (
            <SentConfirmation email={email} onChange={() => setSent(false)} />
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <label className="block">
                <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 inline-block">
                  {t('login.email')}
                </span>
                <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-border/60 bg-card focus-within:border-primary/40 focus-within:ring-1 focus-within:ring-primary/20 transition-colors">
                  <Mail className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  <input
                    type="email"
                    required
                    autoFocus
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('login.emailPlaceholder')}
                    className="w-full bg-transparent text-base text-foreground placeholder:text-muted-foreground/70 focus:outline-none"
                  />
                </div>
              </label>

              <button
                type="submit"
                disabled={req.isPending || !email.trim()}
                className="w-full inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-foreground hover:bg-foreground/90 disabled:opacity-60 disabled:cursor-not-allowed text-background font-semibold text-sm transition-colors"
              >
                {req.isPending ? t('login.sending') : t('login.sendLink')}
                {!req.isPending && <ArrowRight className="w-4 h-4" />}
              </button>

              <p className="text-xs text-muted-foreground text-center mt-6 leading-relaxed">
                {t('login.terms')}
              </p>
            </form>
          )}
        </motion.div>
      </section>
    </SiteShell>
  );
}

function SentConfirmation({
  email,
  onChange,
}: {
  email: string;
  onChange: () => void;
}) {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
      className="rounded-2xl bg-card border border-border/60 p-8 text-center"
    >
      <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
        <CheckCircle2 className="w-7 h-7 text-primary" />
      </div>
      <h2 className="text-2xl font-bold text-foreground mb-3">
        {t('login.checkInboxTitle')}
      </h2>
      <p className="text-muted-foreground leading-relaxed mb-6">
        <Trans
          i18nKey="login.checkInboxBody"
          values={{ email }}
          components={[<span className="text-foreground font-medium" />]}
        />
      </p>
      <button
        type="button"
        onClick={onChange}
        className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4"
      >
        {t('login.useDifferentEmail')}
      </button>
    </motion.div>
  );
}

// ─── Consume ───────────────────────────────────────────────────────────

function ConsumeMagicLink({ token, next }: { token: string; next: string }) {
  const { t } = useTranslation();
  const consume = useConsumeMagicLink();
  const me = useMe();
  const nav = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    consume.mutate(token, {
      onSuccess: () => {
        nav(next, { replace: true });
      },
      onError: (e) => {
        setError((e as Error).message || t('login.consumeFailedDefault'));
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // If we somehow land here already signed-in, bounce.
  useEffect(() => {
    if (me.data && !consume.isPending) {
      nav(next, { replace: true });
    }
  }, [me.data, consume.isPending, nav, next]);

  return (
    <SiteShell>
      <section className="max-w-md mx-auto px-6 py-24 md:py-32 text-center">
        {error ? (
          <>
            <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-4">
              {t('login.eyebrow')}
            </p>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('login.consumeFailedTitle')}
            </h1>
            <p className="text-muted-foreground mb-8">{error}</p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity"
            >
              {t('common.tryAgain')}
            </Link>
          </>
        ) : (
          <>
            <div className="w-12 h-12 rounded-full border-2 border-primary/30 border-t-primary animate-spin mx-auto mb-6" />
            <p className="text-muted-foreground">{t('login.consumingTitle')}</p>
          </>
        )}
      </section>
    </SiteShell>
  );
}
